import { useContext } from 'react';
import isPropValid from '@emotion/is-prop-valid';
import styled from '@emotion/styled';
import {
  createDomMotionComponent,
  useMotionTemplate,
  useMotionValue,
} from 'framer-motion';

import * as Accent from '@/components/common/Accent';
import { PageThemeContext } from '@/contexts/PageThemeContext';

import type { HeadingSizeTypeLegacy } from '@/components/common/legacyMarkUp';
import type { HeadingSizeTypeV2 } from '@/components/common/MarkUp';
import type { MotionValue } from 'framer-motion';
import type { MutableRefObject } from 'react';

const motion = {
  span: createDomMotionComponent('span'),
};

const SvgWrapper = styled(motion.span, { shouldForwardProp: isPropValid })<{
  colour: string;
}>`
  color: ${(props) => `var(--colour-${props.colour})`};
  position: absolute;

  svg {
    height: 100%;
    width: 100%;
    position: absolute;
    left: 0;
    top: 0;
  }
`;

/**
 * Highlight
 *
 * Each highlight svg needs to render slightly differently.
 * All dynamic sizing is controlled within Framer Motion so that changes
 * to sizing bypass the normal react render methods and allow us to
 * independently animate the svgs in the future
 *
 */

interface HighlightProps {
  style:
    | 'underline'
    | 'thin-underline'
    | 'double'
    | 'double-thin'
    | 'squiggle'
    | 'thick'
    | 'doubleCircle'
    | 'circle'
    | 'arc'
    | 'tall-arc'
    | 'ray'
    | 'ray-right'
    | 'arrows'
    | 'arrows-top'
    | 'pigs-tail';
  colour?: string;
  overrideColour?: string;
  layoutRef: MutableRefObject<{
    width: MotionValue<any>;
    height: MotionValue<any>;
    left: MotionValue<any>;
    top: MotionValue<any>;
    opacity: MotionValue<any>;
  }>;
  size?: HeadingSizeTypeLegacy | HeadingSizeTypeV2;
}

const Highlight = ({
  style,
  colour,
  overrideColour,
  layoutRef,
  size,
}: HighlightProps) => {
  const { width, height, left, top, opacity } = layoutRef.current;

  const isSubheading = [
    'subheading-sm',
    'subheading-md',
    'subheading-lg',
  ].includes(size);

  const adjustmentValue = isSubheading ? 4 : 0;
  const adjustment = useMotionValue(adjustmentValue);

  const pageTheme = useContext(PageThemeContext);
  const highlightColour =
    pageTheme?.pickColour(overrideColour || colour, 'primary', 'electron') ||
    overrideColour ||
    colour ||
    'electron';

  switch (style) {
    case 'circle':
      return (
        <SvgWrapper
          colour={highlightColour}
          style={{
            left: useMotionTemplate`calc(${left}px - 0.4em)`,
            width: useMotionTemplate`calc(${width}px + 0.8em)`,
            top: useMotionTemplate`calc(${top}px - 0.1em)`,
            height: useMotionTemplate`calc(${height}px + 0.4em)`,
            opacity,
            transform: 'rotate(9deg)',
            transformOrigin: 'center',
          }}
        >
          <Accent.Circle variant="single" />
        </SvgWrapper>
      );
    case 'doubleCircle':
      return (
        <SvgWrapper
          colour={highlightColour}
          style={{
            left: useMotionTemplate`calc(${left}px - 0.1em)`,
            width: useMotionTemplate`calc(${width}px + 0.2em)`,
            top: useMotionTemplate`calc(${top}px + 0.09em)`,
            height: useMotionTemplate`calc(${height}px + 0.2em)`,
            opacity,
            transform: 'rotate(358deg)',
            transformOrigin: 'center',
          }}
        >
          <Accent.Circle variant="double" />
        </SvgWrapper>
      );
    case 'underline':
      return (
        <SvgWrapper
          colour={highlightColour}
          style={{
            left,
            width,
            top: useMotionTemplate`calc(${top}px + ${height}px + ${adjustment}px - 0.2em)`,
            height: '0.12em',
            opacity,
          }}
        >
          <Accent.Underline variant="short" />
        </SvgWrapper>
      );
    case 'thin-underline':
      return (
        <SvgWrapper
          colour={highlightColour}
          style={{
            left,
            width,
            top: useMotionTemplate`calc(${top}px + ${height}px - 0.15em)`,
            height: '0.07em',
            opacity,
          }}
        >
          <Accent.Underline variant="short" />
        </SvgWrapper>
      );
    case 'double':
      return (
        <SvgWrapper
          colour={highlightColour}
          style={{
            left,
            width,
            top: useMotionTemplate`calc(${top}px + ${height}px + ${adjustment}px - 0.2em)`,
            height: '0.25em',
            opacity,
          }}
        >
          <Accent.Underline variant="double" />
        </SvgWrapper>
      );

    case 'double-thin':
      return (
        <SvgWrapper
          colour={highlightColour}
          style={{
            left,
            width,
            top: useMotionTemplate`calc(${top}px + ${height}px - 0.2em)`,
            height: '0.12em',
            opacity,
          }}
        >
          <Accent.Underline variant="double" />
        </SvgWrapper>
      );
    case 'squiggle':
      return (
        <SvgWrapper
          colour={highlightColour}
          style={{
            left,
            width,
            top: useMotionTemplate`calc(${top}px + ${height}px - 0.2em)`,
            height: '0.22em',
            opacity,
          }}
        >
          <Accent.Underline variant="wave" />
        </SvgWrapper>
      );
    case 'arc':
      return (
        <SvgWrapper
          colour={highlightColour}
          style={{
            left,
            width,
            top: '-0.5em',
            height: '0.5em',
            opacity,
          }}
        >
          <Accent.Emphasise variant="arc" />
        </SvgWrapper>
      );
    case 'tall-arc':
      return (
        <SvgWrapper
          colour={highlightColour}
          style={{
            left: useMotionTemplate`calc(${left}px + 0.4em)`,
            width: '3.25em',
            top: useMotionTemplate`calc(${top}px - 0.75em)`,
            height: '1em',
            opacity,
          }}
        >
          <Accent.Emphasise variant="arc" />
        </SvgWrapper>
      );
    case 'thick':
      return (
        <SvgWrapper
          colour={highlightColour}
          style={{
            left: useMotionTemplate`calc(${left}px - 0.1em)`,
            width: useMotionTemplate`calc(${width}px + 0.2em)`,
            top,
            height,
            opacity,
            transform: 'rotate(-5deg)',
            transformOrigin: 'center',
          }}
        >
          <Accent.Underline variant="thick" />
        </SvgWrapper>
      );
    case 'ray':
      return (
        <SvgWrapper
          colour={highlightColour}
          style={{
            left: useMotionTemplate`calc(${left}px - 0.35em)`,
            width: useMotionTemplate`calc(0.5em)`,
            height: useMotionTemplate`calc(0.5em)`,
            top: useMotionTemplate`calc(${top}px - 0.15em)`,
            opacity,
            transform: 'rotate(-45deg)',
            transformOrigin: 'center',
          }}
        >
          <Accent.Emphasise variant="ray" />
        </SvgWrapper>
      );
    case 'ray-right':
      return (
        <SvgWrapper
          colour={highlightColour}
          style={{
            left: useMotionTemplate`calc(${left}px + ${width}px)`,
            width: useMotionTemplate`calc(0.5em)`,
            height: useMotionTemplate`calc(0.5em)`,
            top: useMotionTemplate`calc(${top}px + 0.1em)`,
            opacity,
            transform: 'rotate(60deg) scale(1.75,1.75)',
            transformOrigin: 'center',
          }}
        >
          <Accent.Emphasise variant="ray" />
        </SvgWrapper>
      );
    case 'arrows':
      return (
        <SvgWrapper
          colour={highlightColour}
          style={{
            left: useMotionTemplate`calc(${left}px + ${width}px + 0.1em)`,
            width: '0.7em',
            height: '0.7em',
            top: useMotionTemplate`calc(${top}px + 0.2em)`,
            opacity,
            transform: 'rotate(-25deg) ',
            transformOrigin: 'center',
          }}
        >
          <Accent.Arrows variant="trio" />
        </SvgWrapper>
      );
    case 'arrows-top':
      return (
        <SvgWrapper
          colour={highlightColour}
          style={{
            left: useMotionTemplate`calc(${left}px + ${width}px)`,
            width: '1.5em',
            height: '1.5em',
            top: useMotionTemplate`calc(${top}px - 0.75em)`,
            opacity,
            transform: 'rotate(0.375turn) scale(-100%,100%)',
            transformOrigin: 'center',
          }}
        >
          <Accent.Arrows variant="trio" />
        </SvgWrapper>
      );
    case 'pigs-tail':
      return (
        <SvgWrapper
          colour={highlightColour}
          style={{
            left: useMotionTemplate`calc(${left}px + ${width}px + 0.1em)`,
            width: useMotionTemplate`calc(0.5em)`,
            height: useMotionTemplate`calc(0.5em)`,
            top: useMotionTemplate`calc(${top}px + 0.1em)`,
            opacity,
            transform: 'rotate(-60deg) scale(2,2)',
            transformOrigin: 'center',
          }}
        >
          <Accent.Emphasise variant="pigs-tail" />
        </SvgWrapper>
      );
    default:
      return null;
  }
};

export { Highlight };
